<template>
  <div class="shrink-0 bg-white border-b border-gray-200">
    <div v-if="!expanded" class="mc-week">
      <button
        v-for="c in weekCells"
        :key="c.key"
        class="mc-cell"
        :class="{ 'mc-on': selectedDue === c.due, 'mc-over': c.kind === 'overdue', 'mc-today': c.kind === 'today' }"
        @click="toggle(c.due)"
      >
        <div class="mc-cell-h">
          <span class="mc-d">{{ c.label }}</span>
          <span v-if="c.sub" class="mc-dt">{{ c.sub }}</span>
          <span class="mc-n">{{ loading ? '·' : c.count }}</span>
        </div>
        <template v-if="c.events">
          <span v-for="(e, i) in c.events.slice(0, 2)" :key="i" class="mc-ev" :class="'mc-ev-' + e.kind">
            <FontAwesomeIcon :icon="['fas', e.kind === 'appointment' ? 'stethoscope' : 'phone']" class="mc-ev-ico" />
            <b v-if="e.time">{{ e.time }}</b>
            <span class="mc-ev-t sensitive">{{ e.text }}</span>
          </span>
          <span v-if="c.events.length > 2 || (c.count > c.events.length)" class="mc-more">
            {{ c.events.length > 2 ? `+${c.events.length - 2} more` : `+${c.count - c.events.length} due` }}
          </span>
        </template>
        <span v-else-if="c.note" class="mc-more">{{ c.note }}</span>
      </button>
      <button class="mc-toggle" @click="expand">
        <FontAwesomeIcon :icon="['fas', 'calendar']" />Month
      </button>
    </div>

    <div v-else>
      <div class="mc-mon-h">
        <button class="mc-nav" @click="shiftMonth(-1)"><FontAwesomeIcon :icon="['fas', 'chevron-left']" /></button>
        <span class="mc-mon-t">{{ monthLabel }}</span>
        <button class="mc-nav" @click="shiftMonth(1)"><FontAwesomeIcon :icon="['fas', 'chevron-right']" /></button>
        <span class="mc-sum">
          <button class="mc-sum-ov" @click="toggle('overdue')"><b>{{ cal.overdue || 0 }}</b> overdue</button>
          <span><b>{{ dayCount(todayIso) }}</b> due today</span>
          <span><b>{{ monthDue }}</b> patient-days due</span>
          <span v-if="appointmentTotal"><b>{{ appointmentTotal }}</b> appointments</span>
        </span>
        <button class="mc-collapse" @click="collapse">
          <FontAwesomeIcon :icon="['fas', 'chevron-up']" style="font-size: 9px;" />Week
        </button>
      </div>
      <div class="mc-grid">
        <div v-for="d in ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']" :key="d" class="mc-dow">{{ d }}</div>
        <button
          v-for="c in monthCells"
          :key="c.iso"
          class="mc-dc"
          :class="{
            'mc-out': !c.inMonth, 'mc-we': c.weekend, 'mc-past': c.past,
            'mc-dc-today': c.iso === todayIso, 'mc-on': selectedDue === c.iso,
          }"
          @click="c.past ? toggle('overdue') : toggle(c.iso)"
        >
          <div class="mc-dtop">
            <span class="mc-num">{{ c.day === 1 ? `${MON[c.month]} 1` : c.day }}</span>
            <span v-if="!c.past && c.count" class="mc-cnt" :class="{ 'mc-cnt-hot': c.count >= 8 }">{{ c.count }}</span>
          </div>
          <span v-for="(e, i) in c.events.slice(0, 2)" :key="i" class="mc-ev" :class="'mc-ev-' + e.kind">
            <FontAwesomeIcon :icon="['fas', e.kind === 'appointment' ? 'stethoscope' : 'phone']" class="mc-ev-ico" />
            <b v-if="e.time">{{ e.time }}</b>
            <span class="mc-ev-t sensitive">{{ e.text }}</span>
          </span>
          <span v-if="c.events.length > 2" class="mc-more">+{{ c.events.length - 2 }} more</span>
        </button>
      </div>
    </div>

    <div v-if="selectedDue" class="mc-bar">
      <FontAwesomeIcon :icon="['fas', 'filter']" style="font-size: 10px;" />
      Showing <b>{{ filteredTotal }}</b> patient{{ filteredTotal === 1 ? '' : 's' }} with work due <b>{{ dueLabel }}</b>
      <span class="mc-legend">
        <span><FontAwesomeIcon :icon="['fas', 'phone']" class="mc-ev-ico mc-ev-booked" />booked time</span>
        <span v-if="hasAppointments"><FontAwesomeIcon :icon="['fas', 'stethoscope']" class="mc-ev-ico mc-ev-appointment" />patient appointment</span>
      </span>
      <button class="mc-clear" @click="$emit('select-due', null)">Clear ✕</button>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { formatDueTime } from '@/utils/common';

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DEFAULT_TIME = '9:00 AM';
const WEEK_DAYS = 5;
const CARD_DAYS = 13;

function iso(d) {
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function fromIso(s) {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export default {
  name: 'MyTasksCalendar',
  components: { FontAwesomeIcon },
  props: {
    scopeParams: { type: Object, required: true },
    selectedDue: { type: String, default: null },
    filteredTotal: { type: Number, default: 0 },
    expandedProp: { type: Boolean, default: false },
    refreshKey: { type: Number, default: 0 },
  },
  emits: ['select-due', 'patient-events', 'expanded'],
  data() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return {
      MON,
      today,
      expanded: this.expandedProp,
      monthAnchor: new Date(today.getFullYear(), today.getMonth(), 1),
      loading: false,
      cal: { days: {}, overdue: 0, later: 0 },
      appts: { days: {}, by_entity: {} },
    };
  },
  computed: {
    todayIso() {
      return iso(this.today);
    },
    weekDays() {
      const out = [];
      let d = new Date(this.today);
      while (out.length < WEEK_DAYS) {
        if (d.getDay() !== 0 && d.getDay() !== 6) out.push(new Date(d));
        d = addDays(d, 1);
      }
      return out;
    },
    weekEnd() {
      return this.weekDays[this.weekDays.length - 1];
    },
    range() {
      if (!this.expanded) return { from: this.todayIso, to: iso(this.weekEnd) };
      const cells = this.monthCells;
      return { from: cells[0].iso, to: cells[cells.length - 1].iso };
    },
    weekCells() {
      const cells = [{
        key: 'overdue', kind: 'overdue', label: 'Overdue', due: 'overdue', count: this.cal.overdue || 0,
        note: this.cal.overdue ? 'still open' : 'none',
      }];
      this.weekDays.forEach((d, i) => {
        const k = iso(d);
        cells.push({
          key: k, kind: i === 0 && k === this.todayIso ? 'today' : 'day',
          label: k === this.todayIso ? 'Today' : DOW[d.getDay()],
          sub: `${MON[d.getMonth()]} ${d.getDate()}`, due: k,
          count: this.dayCount(k), events: this.eventsFor(k),
        });
      });
      cells.push({
        key: 'later', kind: 'later', label: 'Later', due: `after:${iso(this.weekEnd)}`,
        count: this.cal.later || 0, note: this.cal.later ? 'beyond this week' : 'nothing scheduled',
      });
      return cells;
    },
    monthLabel() {
      return `${this.monthAnchor.toLocaleString('en-US', { month: 'long' })} ${this.monthAnchor.getFullYear()}`;
    },
    monthCells() {
      const first = this.monthAnchor;
      const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
      const isCurrent = first.getFullYear() === this.today.getFullYear() && first.getMonth() === this.today.getMonth();
      const fromDay = isCurrent ? this.today : first;
      const start = addDays(fromDay, -((fromDay.getDay() + 6) % 7));
      const end = addDays(last, (7 - last.getDay()) % 7);
      const out = [];
      for (let d = new Date(start); d <= end; d = addDays(d, 1)) {
        const k = iso(d);
        out.push({
          iso: k, day: d.getDate(), month: d.getMonth(),
          inMonth: d.getMonth() === first.getMonth(), weekend: d.getDay() === 0 || d.getDay() === 6,
          past: d < this.today, count: this.dayCount(k), events: this.eventsFor(k),
        });
      }
      return out;
    },
    monthDue() {
      return this.monthCells.filter(c => c.inMonth && !c.past).reduce((a, c) => a + c.count, 0);
    },
    appointmentTotal() {
      return this.monthCells.filter(c => c.inMonth).reduce((a, c) => a + (this.appts.days[c.iso] || []).length, 0);
    },
    hasAppointments() {
      return Object.keys(this.appts.days || {}).length > 0;
    },
    dueLabel() {
      if (this.selectedDue === 'overdue') return 'overdue';
      if ((this.selectedDue || '').startsWith('after:')) return `after ${this.shortLabel(this.selectedDue.slice(6))}`;
      return this.selectedDue === this.todayIso ? 'today' : this.shortLabel(this.selectedDue);
    },
    fetchKey() {
      return JSON.stringify([this.scopeParams, this.range, this.refreshKey]);
    },
  },
  watch: {
    expandedProp(v) {
      if (v !== this.expanded) {
        if (v) this.expanded = true;
        else this.collapse();
      }
    },
    fetchKey: {
      handler() { this.load(); },
      immediate: true,
    },
  },
  methods: {
    expand() {
      this.expanded = true;
      this.$emit('expanded', true);
    },
    collapse() {
      this.expanded = false;
      this.monthAnchor = new Date(this.today.getFullYear(), this.today.getMonth(), 1);
      this.$emit('expanded', false);
    },
    shiftMonth(n) {
      this.monthAnchor = new Date(this.monthAnchor.getFullYear(), this.monthAnchor.getMonth() + n, 1);
    },
    toggle(due) {
      this.$emit('select-due', this.selectedDue === due ? null : due);
    },
    dayCount(k) {
      return (this.cal.days[k] || {}).patients || 0;
    },
    bookedFor(k) {
      return ((this.cal.days[k] || {}).items || [])
        .map(i => ({ ...i, time: formatDueTime(i.due_time, i.due_date) }))
        .filter(i => i.time && i.time !== DEFAULT_TIME);
    },
    eventsFor(k) {
      const booked = this.bookedFor(k).map(i => ({ kind: 'booked', time: i.time, text: this.surname(i.patient_name) }));
      const appts = (this.appts.days[k] || []).map(a => ({ kind: 'appointment', time: '', text: `${this.surname(a.patient_name)} · ${a.label}` }));
      return [...booked, ...appts];
    },
    surname(name) {
      const s = (name || '').split(',')[0].trim();
      return s ? s.charAt(0) + s.slice(1).toLowerCase() : '';
    },
    shortLabel(k) {
      const d = fromIso(k);
      return `${DOW[d.getDay()]} ${MON[d.getMonth()]} ${d.getDate()}`;
    },
    async load() {
      const seq = (this._seq = (this._seq || 0) + 1);
      this.loading = true;
      const params = { ...this.scopeParams, from: this.range.from, to: this.range.to };
      const apptTo = iso(addDays(this.today, CARD_DAYS));
      const apptParams = { ...this.scopeParams, from: this.range.from < this.todayIso ? this.range.from : this.todayIso, to: this.range.to > apptTo ? this.range.to : apptTo };
      try {
        const { data } = await this.$axios.get('/api/careplannodes/calendar/', { params });
        if (seq !== this._seq) return;
        if (data?.success) this.cal = data;
      } catch (e) {
        console.error('Error loading calendar:', e);
      } finally {
        if (seq === this._seq) this.loading = false;
      }
      this.emitPatientEvents();
      try {
        const { data } = await this.$axios.get('/api/careplannodes/appointments/', { params: apptParams });
        if (seq !== this._seq) return;
        if (data?.success) this.appts = data;
      } catch (e) {
        console.error('Error loading appointments:', e);
      }
      this.emitPatientEvents();
    },
    emitPatientEvents() {
      const horizon = iso(addDays(this.today, CARD_DAYS));
      const byEntity = {};
      Object.keys(this.cal.days || {}).forEach(k => {
        if (k < this.todayIso || k > horizon) return;
        this.bookedFor(k).forEach(i => {
          (byEntity[i.entity_id] = byEntity[i.entity_id] || []).push({ kind: 'booked', date: k, time: i.time, label: i.label });
        });
      });
      Object.values(this.appts.by_entity || {}).forEach(list => {
        list.forEach(a => {
          if (a.date < this.todayIso || a.date > horizon) return;
          (byEntity[a.entity_id] = byEntity[a.entity_id] || []).push({ kind: 'appointment', date: a.date, time: '', label: a.label, clinician: a.clinician });
        });
      });
      Object.values(byEntity).forEach(l => l.sort((a, b) => (a.date + (a.time || '')).localeCompare(b.date + (b.time || ''))));
      this.$emit('patient-events', byEntity);
    },
  },
};
</script>

<style scoped>
.mc-week { display: flex; align-items: stretch; }
.mc-cell {
  flex: 1; min-width: 0; padding: 8px 10px 9px; border: 0; border-right: 1px solid #F3F4F6; background: #fff;
  display: flex; flex-direction: column; gap: 5px; text-align: left; cursor: pointer;
}
.mc-cell:hover { background: #FAFAFA; }
.mc-cell.mc-on { background: #FFF7F3; box-shadow: inset 0 -2px 0 #F76734; }
.mc-cell-h { display: flex; align-items: baseline; gap: 6px; }
.mc-d { font-size: 10px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: #6B7280; }
.mc-dt { font-size: 11px; color: #9CA3AF; }
.mc-n { margin-left: auto; font-size: 15px; font-weight: 700; color: #111827; font-variant-numeric: tabular-nums; }
.mc-over .mc-d, .mc-over .mc-n { color: #B45309; }
.mc-today .mc-d { color: #B9431B; }
.mc-ev { display: flex; align-items: center; gap: 5px; font-size: 11px; color: #374151; white-space: nowrap; min-width: 0; }
.mc-ev b { font-weight: 600; font-variant-numeric: tabular-nums; color: #111827; flex-shrink: 0; }
.mc-ev-t { overflow: hidden; text-overflow: ellipsis; min-width: 0; }
.mc-ev-ico { font-size: 9px; flex-shrink: 0; margin-right: 3px; }
.mc-ev-booked .mc-ev-ico, .mc-ev-ico.mc-ev-booked { color: #3B6FD9; }
.mc-ev-appointment .mc-ev-ico, .mc-ev-ico.mc-ev-appointment { color: #389A6C; }
.mc-more { font-size: 10.5px; color: #9CA3AF; }
.mc-toggle {
  flex: 0 0 76px; display: flex; align-items: center; justify-content: center; gap: 6px; font-size: 11.5px; color: #4B5563;
  border: 0; border-left: 1px solid #E5E7EB; background: #fff; cursor: pointer;
}
.mc-toggle:hover { background: #FAFAFA; }

.mc-mon-h { display: flex; align-items: center; gap: 10px; padding: 8px 14px; border-bottom: 1px solid #F3F4F6; }
.mc-mon-t { font-size: 13px; font-weight: 600; color: #111827; min-width: 124px; text-align: center; }
.mc-nav {
  width: 24px; height: 24px; border: 1px solid #E5E7EB; border-radius: 5px; background: #fff;
  display: inline-flex; align-items: center; justify-content: center; color: #6B7280; font-size: 10px; cursor: pointer;
}
.mc-sum { display: flex; gap: 14px; margin-left: 10px; font-size: 11.5px; color: #6B7280; }
.mc-sum b { color: #111827; font-weight: 600; }
.mc-sum-ov { border: 0; background: none; padding: 0; color: #6B7280; cursor: pointer; font-size: 11.5px; }
.mc-sum-ov b { color: #B45309; }
.mc-collapse {
  margin-left: auto; display: flex; align-items: center; gap: 5px; font-size: 11.5px; color: #4B5563; cursor: pointer;
  padding: 4px 8px; border: 1px solid #E5E7EB; border-radius: 6px; background: #fff;
}
.mc-grid { display: grid; grid-template-columns: repeat(7, 1fr); }
.mc-dow { padding: 5px 8px; font-size: 10px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: #9CA3AF; border-bottom: 1px solid #F3F4F6; }
.mc-dc {
  min-height: 74px; padding: 5px 7px 6px; border: 0; border-right: 1px solid #F3F4F6; border-bottom: 1px solid #F3F4F6;
  background: #fff; display: flex; flex-direction: column; gap: 3px; cursor: pointer; min-width: 0; text-align: left;
}
.mc-dc:nth-child(7n) { border-right: 0; }
.mc-dc:hover { background: #FAFAFA; }
.mc-dc.mc-out, .mc-dc.mc-we { background: #FBFBFB; }
.mc-dc.mc-out .mc-num, .mc-dc.mc-past .mc-num { color: #C7C9CC; }
.mc-dc.mc-dc-today { background: #FFF7F3; box-shadow: inset 0 0 0 1.5px #F76734; }
.mc-dc.mc-on { box-shadow: inset 0 0 0 1.5px #F76734; }
.mc-dtop { display: flex; align-items: center; gap: 6px; }
.mc-num { font-size: 11.5px; font-weight: 600; color: #374151; }
.mc-dc-today .mc-num { color: #fff; background: #F76734; border-radius: 999px; padding: 0 6px; }
.mc-cnt { margin-left: auto; font-size: 10.5px; font-weight: 700; padding: 0 6px; border-radius: 999px; background: #F3F4F6; color: #4B5563; font-variant-numeric: tabular-nums; }
.mc-cnt-hot { background: #FEF1EB; color: #B9431B; }
.mc-dc .mc-ev { font-size: 10.5px; }

.mc-bar {
  display: flex; align-items: center; gap: 8px; padding: 6px 14px; background: #FFF7F3; border-top: 1px solid #FDE3D8;
  font-size: 12px; color: #7A3A20;
}
.mc-bar b { font-weight: 600; }
.mc-legend { display: flex; align-items: center; gap: 12px; margin-left: 14px; color: #6B7280; font-size: 11px; }
.mc-clear { margin-left: auto; color: #B9431B; cursor: pointer; font-size: 11.5px; border: 0; background: none; }
</style>
